import React, { useMemo } from 'react';
import { useGlobalContext } from '../context/GlobalStore';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Grid, Typography, List, ListItem, ListItemText, Box, Card, CardContent, CardHeader, Divider } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import Layout from '../mui-components/Layout';
import { skuToNameMap, orderStatusToNameMap } from '../utils/helpers';
import LoadingDialog from '../mui-components/LoadingDialog';
import StatCard from '../mui-components/StatCard';
import { TrendingUp, BarChart as BarChartIcon, HourglassEmpty, Devices as DevicesIcon, ShoppingCart as ShoppingCartIcon } from '@mui/icons-material';

const Dashboard = () => {
  // Chart configuration constants
  const CHART_HEIGHT = 300;
  const ORDERS_CHART_HEIGHT = 250;
  const CHART_LEFT_MARGIN = 140;
  const CHART_TOP_MARGIN = 50;
  const ORDERS_CHART_LEFT_MARGIN = 120;
  const DAYS_THRESHOLD = 30;

  const navigate = useNavigate();
  const { state } = useGlobalContext();
  const { devices, orders, account, isDeviceLoading, isOrdersLoading, apiKey } = state;

  const apiUsageStats = useMemo(() => {
    if (!apiKey?.key_id || !account?.usage_data?.[apiKey?.key_id]) {
      return { highest: 0, average: 0, remaining: 0 };
    }
    const usageData = account.usage_data[apiKey.key_id];
    if (!usageData || usageData.length === 0) {
      return { highest: 0, average: 0, remaining: 0 };
    }
    const usedValues = usageData.map(item => item[1]);
    const highest = Math.max(...usedValues);
    const average = Math.round(usedValues.reduce((sum, current) => sum + current, 0) / usedValues.length);
    const remaining = usageData[usageData.length - 1][2];
    return { highest, average, remaining };
  }, [apiKey?.key_id, account?.usage_data]);

  const devicesUpForRenewalCount = useMemo(() => {
    if (!devices) return 0;
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + DAYS_THRESHOLD);
    thirtyDaysFromNow.setHours(23, 59, 59, 999);
    return devices.filter(device => {
      if (!device.renewal_date) return false;
      const renewalDate = new Date(device.renewal_date);
      if (isNaN(renewalDate.getTime())) return false;
      renewalDate.setHours(0, 0, 0, 0);
      return renewalDate >= now && renewalDate <= thirtyDaysFromNow;
    }).length;
  }, [devices]);

  // Sort by total device count (descending)
  const sortModelsByTotalCount = (sortedDeviceModels, data) => {
    const modelTotals = sortedDeviceModels.map((model, index) => ({
      model,
      total: data[model]['Active'] + data[model]['Preactive'] + data[model]['Pending Deactivation'] + data[model]['Deactivated'],
      originalIndex: index
    }));

    return modelTotals
      .sort((a, b) => b.total - a.total)
      .map(item => item.originalIndex);
  };

  // Format model labels with SKU information
  const formatModelLabel = (model, sku) => {
    if (!sku) return model;
    
    // Always put SKU on a new line for consistency and readability
    return `${model}\n(${sku})`;
  };

  // Static mappings that don't need to be recreated on every render
  const STATUS_NAME_MAPPING = {
    'active': 'Active',
    'preactive': 'Preactive',
    'pending_deactivation': 'Pending Deactivation',
    'deactivated': 'Deactivated'
  };

  const STATUS_COLORS = {
    'Deactivated': '#DDDDDD',
    'Pending Deactivation': '#BFD3E6',
    'Preactive': '#95BCE6',
    'Active': '#005D9E'
  };

  // Status to query parameter mapping for navigation
  const STATUS_TO_QUERY_PARAM = {
    'Active': 'active',
    'Preactive': 'preactive',
    'Pending Deactivation': 'pending_deactivation',
    'Deactivated': 'deactivated'
  };

  const { models, series, nameToSkuMap, deviceModels } = useMemo(() => {
    if (!devices || devices.length === 0) return { models: [], series: [], deviceModels: [] };

    const data = {};
    const sortedDeviceModels = [...new Set(devices.map(d => skuToNameMap[d.device_model] || d.device_model || 'Unknown'))].sort();
    const statuses = ['Deactivated', 'Pending Deactivation', 'Preactive', 'Active'];

    sortedDeviceModels.forEach(model => {
      data[model] = {
        'Active': 0,
        'Preactive': 0,
        'Pending Deactivation': 0,
        'Deactivated': 0,
      };
    });

    devices.forEach(device => {
      const model = skuToNameMap[device.device_model] || device.device_model || 'Unknown';
      const rawStatus = device.billing_status || 'unknown';
      const formattedStatus = STATUS_NAME_MAPPING[rawStatus.toLowerCase()];
      if (data[model] && formattedStatus) {
        data[model][formattedStatus]++;
      }
    });

    const chartSeries = statuses.map(status => ({
      data: sortedDeviceModels.map(model => data[model][status]),
      label: status,
      color: STATUS_COLORS[status],
      stack: 'total',
      valueFormatter: value => value > 0 ? value.toString() : ''
    }));

    const nameToSkuMap = devices.reduce((acc, device) => {
      const name = skuToNameMap[device.device_model] || device.device_model || 'Unknown';
      if (name !== 'Unknown' && !acc[name]) {
        acc[name] = device.device_model;
      }
      return acc;
    }, {});

    // Create labels with SKU information - format for better readability
    const modelsWithSku = sortedDeviceModels.map(model => {
      const sku = nameToSkuMap[model];
      return formatModelLabel(model, sku);
    });

    const sortedIndices = sortModelsByTotalCount(sortedDeviceModels, data);

    // Reorder the data based on sorted indices
    const sortedModels = sortedIndices.map(index => modelsWithSku[index]);
    const sortedSeries = statuses.map(status => ({
      ...chartSeries.find(s => s.label === status),
      data: sortedIndices.map(index => data[sortedDeviceModels[index]][status])
    }));

    return { 
      models: sortedModels, 
      series: sortedSeries, 
      nameToSkuMap, 
      deviceModels: sortedIndices.map(index => sortedDeviceModels[index])
    };
  }, [devices]);

  const ordersLast30Days = useMemo(() => {
    if (!orders) return [];
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - DAYS_THRESHOLD);
    return orders.filter(order => new Date(order.date_placed) >= thirtyDaysAgo);
  }, [orders]);

  const ordersLast30DaysChartData = useMemo(() => {
    const statusCounts = ordersLast30Days.reduce((acc, order) => {
      const status = orderStatusToNameMap[order.order_status] || order.order_status || 'Unknown';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(statusCounts).map(([status, value]) => ({ status, value }));
  }, [ordersLast30Days]);

  return (
    <Layout title="Dashboard">
      <LoadingDialog open={!!(isDeviceLoading || isOrdersLoading)} />
      <Grid container spacing={3}>

        {apiKey?.key_id && (
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Pylo API Usage" sx={{ mb: -1 }} />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>Last 30 days</Typography>
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={4}><StatCard title="Highest Daily Usage" value={apiUsageStats.highest.toLocaleString()} icon={<TrendingUp fontSize="large" color="primary" />} /></Grid>
                  <Grid item xs={12} sm={4}><StatCard title="Average Daily Usage" value={apiUsageStats.average.toLocaleString()} icon={<BarChartIcon fontSize="large" color="primary" />} /></Grid>
                  <Grid item xs={12} sm={4}><StatCard title="Monthly Usage Remaining" value={apiUsageStats.remaining.toLocaleString()} icon={<HourglassEmpty fontSize="large" color="primary" />} /></Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        <Grid item xs={12}>
          <Card>
            <CardHeader title="Device Overview" />
            <CardContent>
              {devices && devices.length > 0 ? (
                <Grid container spacing={2} direction="column">
                  <Grid item xs={12} sx={{ width: '100%' }}>
                      <Box sx={{ height: CHART_HEIGHT, width: '100%' }}>
                       <BarChart
                         layout="horizontal"
                         xAxis={[{ valueFormatter: (value) => Number.isInteger(value) ? value.toString() : '' }]}
                         yAxis={[{ 
                           scaleType: 'band', 
                           data: models, 
                           tickLabelStyle: { 
                             fontSize: 12,
                             whiteSpace: 'pre-line',
                             lineHeight: 1.1
                           } 
                         }]}
                         series={series}
                         margin={{ left: CHART_LEFT_MARGIN, top: CHART_TOP_MARGIN }}
                          onItemClick={(event, d) => {
                           if (!d) return;
                           
                           const statusLabel = d.seriesId;
                           const statusQuery = STATUS_TO_QUERY_PARAM[statusLabel];
                           
                           // Extract the original model name (without SKU) for the query
                           const originalModelName = deviceModels[d.dataIndex];
                           const modelQuery = nameToSkuMap[originalModelName] || originalModelName;

                           if (modelQuery && statusQuery) {
                             navigate(`/devices?device_model=${encodeURIComponent(modelQuery)}&billing_status=${encodeURIComponent(statusQuery)}`);
                           } else if (modelQuery) {
                             navigate(`/devices?device_model=${encodeURIComponent(modelQuery)}`);
                           }
                         }}
                        slotProps={{
                          legend: {
                            direction: 'row',
                            position: { vertical: 'top', horizontal: 'middle' },
                            padding: 0,
                            labelStyle: { fontSize: 14 },
                            itemMarkWidth: 14,
                            itemMarkHeight: 14,
                            markGap: 8,
                            itemGap: 24,
                          },
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sx={{ width: '100%' }}>
                    <Divider sx={{ my: 2 }} />
                    <Grid container spacing={2} justifyContent="space-around" sx={{ textAlign: 'center' }}>
                      <Grid item xs={6}>
                        <Typography variant='subtitle1' color="text.secondary">Total Devices</Typography>
                        <Typography component={Link} to="/devices" variant='h4' sx={{ fontWeight: 'bold', textDecoration: 'none', color: 'primary.main' }}>{devices.length}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant='subtitle1' color="text.secondary">Up for Renewal</Typography>
                        <Typography component={Link} to="/devices?renewal_status=pending" variant='h4' sx={{ fontWeight: 'bold', textDecoration: 'none', color: 'primary.main' }}>{devicesUpForRenewalCount}</Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                  <DevicesIcon sx={{ fontSize: 60, color: 'text.secondary' }} />
                  <Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>No Device Data</Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>There is no device data to display for this API key.</Typography>
                  <Button component={Link} to="/placeOrder" variant="contained">Order a Device</Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardHeader title="Orders Overview" />
            <CardContent>
              {ordersLast30Days.length > 0 ? (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant='subtitle1' align="center" sx={{ fontWeight: 'bold' }}>Last 30 Days by Status</Typography>
                      <Box sx={{ height: ORDERS_CHART_HEIGHT }}>
                       <BarChart
                         layout="horizontal"
                         yAxis={[{ scaleType: 'band', data: ordersLast30DaysChartData.map(o => o.status) }]}
                         xAxis={[{ valueFormatter: (value) => Number.isInteger(value) ? value.toString() : '' }]}
                         series={[{ data: ordersLast30DaysChartData.map(o => o.value), valueFormatter: (value) => value.toString(), color: STATUS_COLORS['Active'] }]}
                         margin={{ left: ORDERS_CHART_LEFT_MARGIN }}
                        slotProps={{ legend: { hidden: true } }}
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant='subtitle1' align="center" sx={{ fontWeight: 'bold' }}>Recently Placed</Typography>
                      <Box sx={{ height: ORDERS_CHART_HEIGHT, overflow: 'auto' }}>
                       <List dense>
                        {ordersLast30Days.map((order) => (
                          <div key={order.order_number}>
                            <ListItem button component={Link} to={`/orders/${order.order_number}`}>
                              <ListItemText 
                                primary={order.devices?.map(item => `${item.quantity} x ${skuToNameMap[item.sku] || item.sku}`).join(', ') || 'No items'}
                                secondary={`#${order.order_number.substring(0, 8)}... - ${orderStatusToNameMap[order.order_status] || order.order_status}`}
                                primaryTypographyProps={{  noWrap: true, textOverflow: 'ellipsis' }}
                              />
                              <Typography variant="body2" color="text.secondary">{new Date(order.date_placed).toLocaleDateString()}</Typography>
                            </ListItem>
                            <Divider component="li" />
                          </div>
                        ))}
                      </List>
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ textAlign: 'right' }}>
                      <Button component={Link} to="/orders" variant="outlined">View All Orders</Button>
                    </Box>
                  </Grid>
                </Grid>
              ) : orders && orders.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 2 }}>
                  <ShoppingCartIcon sx={{ fontSize: 60, color: 'text.secondary' }} />
                  <Typography variant="h6" color="text.secondary" sx={{ mt: 1 }}>No Recent Orders</Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>There are no orders in the last 30 days for this API key.</Typography>
                  <Button component={Link} to="/orders" variant="contained">View All Orders</Button>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 2 }}>
                  <ShoppingCartIcon sx={{ fontSize: 60, color: 'text.secondary' }} />
                  <Typography variant="h6" color="text.secondary" sx={{ mt: 1 }}>No Order Data</Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>There is no order data to display for this API key.</Typography>
                  <Button component={Link} to="/placeOrder" variant="contained">Place an Order</Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

      </Grid>
    </Layout>
  );
};

export default Dashboard;
